import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  AccountCircle,
  ProductionQuantityLimits,
  VerifiedUserSharp,
  ListAltRounded,
  CategoryRounded,
  LocationCity,
} from '@mui/icons-material';
import useAuth from './useAuth';
import Dashboard from './Dashboard';
import Profile from './Profile';
import Product from './Product';
import Users from './Users';
import Orders from './Orders';
import Category from './Category';
import Area from './Area';

// Main Layout Component
const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const drawerWidth = isExtraSmall ? 200 : 240;

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'product', label: 'Product', icon: <ProductionQuantityLimits /> },
    { id: 'category', label: 'Category', icon: <CategoryRounded /> },
    { id: 'area', label: 'Area', icon: <LocationCity /> },
    { id: 'users', label: 'Users', icon: <VerifiedUserSharp /> },
    { id: 'orders', label: 'Orders', icon: <ListAltRounded /> },
    { id: 'profile', label: 'Profile', icon: <PersonIcon /> },
    // { id: 'settings', label: 'Settings', icon: <SettingsIcon /> }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const renderContent = () => {
    switch (currentView) {
      case 'profile':
        return <Profile />;
      case 'product':
        return <Product />;
      case 'category':
        return <Category />;
      case 'area':
        return <Area />;
      case 'users':
        return <Users />;
      case 'orders':
        return <Orders />;
      case 'settings':
        return (
          <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
            <Typography
              variant={isExtraSmall ? "h5" : "h4"}
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              Settings
            </Typography>
            <Card sx={{ borderRadius: 2, p: { xs: 1, sm: 2 } }}>
              <CardContent>
                <Typography variant={isExtraSmall ? "body2" : "body1"}>
                  Settings panel will be implemented here for managing application configurations.
                </Typography>
              </CardContent>
            </Card>
          </Container>
        );
      default:
        return <Dashboard />;
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        p: { xs: 1, sm: 2 },
        textAlign: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Typography
          variant={isExtraSmall ? "subtitle1" : "h6"}
          fontWeight="bold"
          color="primary"
        >
          Dhahab Admin
        </Typography>
      </Box>
      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.id}
            onClick={() => {
              setCurrentView(item.id);
              if (isMobile) setMobileOpen(false);
            }}
            selected={currentView === item.id}
            sx={{
              py: { xs: 0.5, sm: 1 },
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon sx={{
              color: currentView === item.id ? 'white' : 'inherit',
              minWidth: { xs: 36, sm: 56 }
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: isExtraSmall ? '0.9rem' : '1rem'
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'primary.main'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant={isExtraSmall ? "subtitle1" : "h6"}
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          >
            Admin Panel
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant={isExtraSmall ? "caption" : "body2"}
              sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}
            >
              Welcome, {user?.username}
            </Typography>
            <IconButton
              size="large"
              onClick={handleMenuClick}
              color="inherit"
            >
              <AccountCircle fontSize={isExtraSmall ? "medium" : "large"} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: isExtraSmall ? 120 : 160 }
        }}
      >
        <MenuItem
          onClick={() => { setCurrentView('profile'); handleMenuClose(); }}
          sx={{ fontSize: isExtraSmall ? '0.9rem' : '1rem' }}
        >
          <PersonIcon sx={{ mr: 1, fontSize: isExtraSmall ? '1rem' : '1.25rem' }} />
          Profile
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{ fontSize: isExtraSmall ? '0.9rem' : '1rem' }}
        >
          <LogoutIcon sx={{ mr: 1, fontSize: isExtraSmall ? '1rem' : '1.25rem' }} />
          Logout
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 7, sm: 8 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh'
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default AdminLayout;